<template>
  <form action="#" class="form-horizontal" @submit.prevent="validateForm">
    <div class="form-group" :class="{ 'has-error': errors.first('name') }">
      <label for="partner-name" class="col-sm-3 control-label">Name</label>
      <div class="col-sm-9">
        <input type="text" class="form-control" id="partner-name" name="name"
          v-model="form.name" v-validate="'required|min:2|max:191'" autocomplete="off">
      </div>
      <div class="col-sm-9 col-sm-offset-3 help-block is-danger" v-show="errors.has('name')">
        {{ errors.first('name') }}
      </div>
    </div>

    <div class="form-group" :class="{ 'has-error': errors.first('slug') }">
      <label for="partner-slug" class="col-sm-3 control-label">Slug</label>
      <div class="col-sm-9">
        <input type="text" class="form-control" id="partner-slug" name="slug"
          v-model="form.slug" v-validate="{ regex: /^[a-z0-9]+(?:-[a-z0-9]+)*$/, max: 191 }" autocomplete="off"
          :placeholder="slugPlaceholder">
      </div>
      <div class="col-sm-9 col-sm-offset-3 help-block text-muted" v-if="!form.slug && form.name">
        Will be generated as: {{ slugPlaceholder }}
      </div>
      <div class="col-sm-9 col-sm-offset-3 help-block is-danger" v-show="errors.has('slug')">
        {{ errors.first('slug') }}
      </div>
    </div>

    <div class="form-group" :class="{ 'has-error': errors.first('category') }">
      <label for="partner-category" class="col-sm-3 control-label">Category</label>
      <div class="col-sm-9">
        <select class="form-control" id="partner-category" name="category"
          v-model="form.category" v-validate="'required'">
          <option v-for="cat in categoryOptions" :key="cat.value" :value="cat.value">{{ cat.label }}</option>
        </select>
      </div>
      <div class="col-sm-9 col-sm-offset-3 help-block is-danger" v-show="errors.has('category')">
        {{ errors.first('category') }}
      </div>
    </div>

    <div class="form-group" :class="{ 'has-error': errors.first('links_to') }">
      <label class="col-sm-3 control-label">Links To</label>
      <div class="col-sm-9">
        <label class="checkbox-inline" v-for="option in linksToOptions" :key="option.value">
          <input type="checkbox" :value="option.value" v-model="form.links_to"> {{ option.label }}
        </label>
        <input type="hidden" name="links_to" v-validate="'required'" v-model="linksToValidation">
      </div>
      <div class="col-sm-9 col-sm-offset-3 help-block text-warning" v-if="form.links_to.includes('reader')">
        <i class="fa fa-warning"></i> Partner linking directly to children is currently unsupported.
      </div>
      <div class="col-sm-9 col-sm-offset-3 help-block is-danger" v-show="errors.has('links_to')">
        At least one link type is required
      </div>
    </div>

    <div class="form-group" :class="{ 'has-error': errors.first('contact_email') }" v-if="!form.parent_id">
      <label for="partner-email" class="col-sm-3 control-label">Contact Email</label>
      <div class="col-sm-9">
        <input type="email" class="form-control" id="partner-email" name="contact_email"
          v-model="form.contact_email" v-validate="'email|max:191'" autocomplete="off">
      </div>
      <div class="col-sm-9 col-sm-offset-3 help-block is-danger" v-show="errors.has('contact_email')">
        {{ errors.first('contact_email') }}
      </div>
    </div>

    <div class="form-group" :class="{ 'has-error': errors.first('website') }">
      <label for="partner-website" class="col-sm-3 control-label">Website</label>
      <div class="col-sm-9">
        <input type="url" class="form-control" id="partner-website" name="website"
          v-model="form.website" v-validate="'url|max:191'" autocomplete="off"
          placeholder="https://example.com/">
      </div>
      <div class="col-sm-9 col-sm-offset-3 help-block text-muted">
        Where users are sent when they click this partner on the challenge website.
      </div>
      <div class="col-sm-9 col-sm-offset-3 help-block is-danger" v-show="errors.has('website')">
        {{ errors.first('website') }}
      </div>
    </div>

    <div class="form-group" :class="{ 'has-error': errors.first('logo') }">
      <label for="partner-logo-file" class="col-sm-3 control-label">Logo</label>
      <div class="col-sm-9">
        <div class="logo-field">
          <div class="logo-field__preview-col" v-if="logoPreviewSrc && !logoPreviewError">
            <img class="logo-field__preview" :src="logoPreviewSrc" alt="Logo preview" @error="logoPreviewError = true">
            <button type="button" class="btn btn-link btn-xs text-danger" @click="removeLogo">
              <i class="fa fa-times"></i> Remove logo
            </button>
          </div>
          <div class="logo-field__controls">
            <div class="logo-field__option">
              <span class="logo-field__option-label">Upload a new logo</span>
              <input type="file" id="partner-logo-file" name="logo" accept="image/png"
                v-validate="'mimes:image/png|size:2048'" @change="onLogoSelected">
              <p class="help-block text-muted">
                PNG only (SVGs handled by the dev team). Trimmed, compressed
                (<a href="https://tinypng.com" target="_blank" rel="noopener">tinypng.com</a>) and no larger than 1024&times;1024px.
              </p>
            </div>
            <div class="logo-field__divider"><span>or</span></div>
            <div class="logo-field__option">
              <span class="logo-field__option-label">Or reuse an existing shared logo by filename</span>
              <input type="text" class="form-control input-sm" id="partner-logo"
                v-model="form.logo_filename" autocomplete="off" placeholder="e.g. english_hubs.png">
            </div>
          </div>
        </div>
      </div>
      <div class="col-sm-9 col-sm-offset-3 help-block is-danger" v-show="errors.has('logo')">
        {{ errors.first('logo') }}
      </div>
    </div>

    <div class="box-footer">
      <loading-button type="submit" :isLoading="isLoading">{{ submitLabel }}</loading-button>
    </div>
  </form>
</template>

<script>
import LoadingButton from '@/components/utils/LoadingButton';

const PARTNER_CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'costa', label: 'Costa' },
  { value: 'english_hub', label: 'English Hub' },
];

// CDN base path for partner logos (no trailing slash, no filename). Mirrors
// ChallengePartner::LOGO_DIR on the API. Hardcoded here on purpose; the API does
// not echo it on every partner.
const LOGO_BASE_URL = 'https://d3vlekh17wu64n.cloudfront.net/challenges/_shared/partners';

export default {
  name: 'ChallengePartnerForm',
  components: { LoadingButton },
  props: {
    value: { type: Object, required: true },
    isLoading: { type: Boolean, default: false },
    submitLabel: { type: String, default: 'Save' },
  },
  data() {
    return {
      linksToOptions: [
        { value: 'user', label: 'Parent' },
        { value: 'reader', label: 'Child' },
        { value: 'organisation', label: 'School' },
      ],
      categoryOptions: PARTNER_CATEGORIES,
      logoObjectUrl: null,
      logoPreviewError: false,
    };
  },
  computed: {
    form() {
      return this.value;
    },
    slugPlaceholder() {
      if (!this.form.name) return '';
      return this.form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    },
    linksToValidation() {
      return this.form.links_to && this.form.links_to.length > 0 ? 'valid' : '';
    },
    logoPreviewSrc() {
      if (this.logoObjectUrl) return this.logoObjectUrl;
      if (this.form.logo_filename) {
        return `${LOGO_BASE_URL}/${this.form.logo_filename}`;
      }
      return null;
    },
  },
  methods: {
    onLogoSelected(event) {
      const file = event.target.files && event.target.files[0];
      if (this.logoObjectUrl) {
        URL.revokeObjectURL(this.logoObjectUrl);
        this.logoObjectUrl = null;
      }
      this.logoPreviewError = false;
      this.form.logoFile = file || null;
      if (file) {
        // The chosen file previews via its object URL. We deliberately don't touch
        // logo_filename here: the API stores the logo under the slugified partner
        // name and returns the authoritative filename in its response.
        this.logoObjectUrl = URL.createObjectURL(file);
        // Picking a file overrides any pending removal.
        this.form.removeLogo = false;
      }
    },
    removeLogo() {
      if (this.logoObjectUrl) {
        URL.revokeObjectURL(this.logoObjectUrl);
        this.logoObjectUrl = null;
      }
      this.logoPreviewError = false;
      this.form.logoFile = null;
      this.form.logo_filename = '';
      // Flag for the API to clear the logo on save (an empty filename alone means
      // "leave unchanged"; this is the explicit remove signal).
      this.form.removeLogo = true;
    },
    validateForm() {
      this.$validator.validateAll().then((result) => {
        if (result && this.form.links_to.length > 0) {
          this.$emit('validated', this.form);
        }
      });
    },
  },
  watch: {
    'form.logo_filename': function watchLogoFilename() {
      this.logoPreviewError = false;
    },
  },
  beforeDestroy() {
    if (this.logoObjectUrl) URL.revokeObjectURL(this.logoObjectUrl);
  },
};
</script>

<style lang="scss" scoped>
.logo-field {
  display: flex;
  align-items: flex-start;
  gap: 12px;

  &__preview-col {
    flex-shrink: 0;
    text-align: center;

    button {
      display: block;
      width: 100%;
      margin-top: 2px;
      padding: 0;
    }
  }

  &__preview {
    width: 120px;
    height: 120px;
    object-fit: contain;
    border: 1px solid #d2d6de;
    padding: 4px;
    background: #fff;
  }

  &__controls {
    flex: 1;
  }

  &__option-label {
    display: block;
    font-weight: 600;
    margin-bottom: 4px;
  }

  &__divider {
    display: flex;
    align-items: center;
    text-align: center;
    color: #999;
    margin: 10px 0;

    &::before,
    &::after {
      content: '';
      flex: 1;
      border-top: 1px solid #e0e0e0;
    }

    span {
      padding: 0 10px;
      font-size: 12px;
      text-transform: uppercase;
    }
  }
}
</style>
